import { ColumnDef } from '@tanstack/react-table'
import { MoreHorizontal, Edit, Trash2, Eye, QrCode, FileJson, FileSpreadsheet as FileSpreadsheetIcon, DownloadCloud, FileStack, Box, Boxes } from 'lucide-react'
import { ObjectStatusPair } from '@/components/ui/object-status-pair'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useState } from 'react'
import type { Object as ObjectEntity } from '@/types/entities'
import { formatSecondsToDHM } from '@/features/objects/utils/formatTime'
import { getResponsiveColumnSize } from '@/lib/utils/responsive-columns'
import { buildIconPath } from '@/lib/imagePaths'
import { EntityIcon } from '@/components/ui/entity-icon'
import { TranslatableText } from '@/components/ui/TranslatableText'

interface ObjectActionsProps {
    object: ObjectEntity
    /** Absent for a role without objects.update: the entry is not offered. */
    onEdit?: (object: ObjectEntity) => void
    /** Absent for a role without objects.delete. */
    onDelete?: (object: ObjectEntity) => void
    onView: (object: ObjectEntity) => void
    onRetrieve?: (object: ObjectEntity) => void
}

function ObjectActions({ object, onEdit, onDelete, onView, onRetrieve }: ObjectActionsProps) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)

    return (
        <div
            className="flex justify-center"
            onClick={(e) => {
                e.stopPropagation()
            }}
            onMouseDown={(e) => {
                e.stopPropagation()
            }}
            onMouseUp={(e) => {
                e.stopPropagation()
            }}
            onPointerDown={(e) => {
                e.stopPropagation()
            }}
            onPointerUp={(e) => {
                e.stopPropagation()
            }}
        >
            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setOpen(true)
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                        onMouseUp={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                        }}
                    >
                        <span className="sr-only">{t('objects.openMenu')}</span>
                        <MoreHorizontal className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="end"
                    side="bottom"
                    sideOffset={5}
                    className="w-56"
                    onCloseAutoFocus={(e) => {
                        e.preventDefault()
                    }}
                >
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation()
                            onView(object)
                            setOpen(false)
                        }}
                    >
                        <Eye className="mr-2 h-4 w-4" />
                        {t('objects.viewObject')}
                    </DropdownMenuItem>
                    {onEdit && (
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation()
                                onEdit(object)
                                setOpen(false)
                            }}
                        >
                            <Edit className="mr-2 h-4 w-4" />
                            {t('objects.editObject')}
                        </DropdownMenuItem>
                    )}
                    {onRetrieve && (
                        <DropdownMenuItem
                            onClick={(e) => {
                                e.stopPropagation()
                                onRetrieve(object)
                                setOpen(false)
                            }}
                        >
                            <DownloadCloud className="mr-2 h-4 w-4" />
                            {t('objects.retrieveObject', 'Retrieve object')}
                        </DropdownMenuItem>
                    )}
                    {onDelete && (
                        <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => {
                                e.stopPropagation()
                                onDelete(object)
                                setOpen(false)
                            }}
                        >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t('objects.deleteObject')}
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}

export function createObjectColumns(
    onDelete: ((object: ObjectEntity) => void) | undefined,
    onEdit: ((object: ObjectEntity) => void) | undefined,
    onView: (object: ObjectEntity) => void,
    onShowQRCode: (objectID: string) => void,
    onShowMetadata?: (objectID: string) => void,
    t: (key: string, defaultValue?: string) => string,
    isService: boolean = false,
    onRetrieve?: (object: ObjectEntity) => void,
    onShowRecoveryChain?: (objectID: string) => void,
    /**
     * Show which kind each row is. Only meaningful when the list is NOT filtered to one kind —
     * with the Objects or Services filter on, every row is the same and a column saying so is
     * noise. With All on, the two are otherwise indistinguishable in the grid.
     */
    showType: boolean = false,
): ColumnDef<ObjectEntity>[] {
    // Calculate responsive column sizes (Object ID column holds ID + description)
    const columnSizes = {
        objectID: getResponsiveColumnSize(280),  // Width for Object ID + description
        objectIcon: 40,  // Fixed width for object icon
        qrCode: 60,  // Fixed width for QR code icon
        metadata: 40,  // Fixed width for metadata icon
        status: getResponsiveColumnSize(70),
        location: getResponsiveColumnSize(130),
        engineerRTO: 85,  // Fixed width to fit header text
        endToEndTime: 85,  // Fixed width to fit header text
        totalRTO: 85,  // Fixed width to fit header text
        retention: 90,  // Fixed width for Retention column
        imported: 85,  // Fixed width for Imported column
        actions: 80  // Fixed width, doesn't scale
    }

    return [
        ...(showType ? [{
            id: 'entityType',
            header: '',
            size: 40,
            minSize: 40,
            maxSize: 40,
            enableSorting: false,
            cell: ({ row }: { row: { original: ObjectEntity } }) => {
                const isServiceRow = row.original.objectService === 'Service'
                // NOT objects.service / objects.object — objects.service is already a nested block of
                // rolled-up-view labels in the locale files, so that key resolves to an object.
                const label = isServiceRow ? t('objects.typeService', 'Service') : t('objects.typeObject', 'Object')
                return (
                    <div className="flex justify-center" title={label} aria-label={label}>
                        {isServiceRow
                            ? <Boxes className="h-4 w-4 text-blue-600" aria-hidden />
                            : <Box className="h-4 w-4 text-blue-600" aria-hidden />}
                    </div>
                )
            },
        } as ColumnDef<ObjectEntity>] : []),
        {
            accessorKey: 'objectID',
            header: t('objects.objectID'),
            size: columnSizes.objectID,
            minSize: 200,
            maxSize: 400,
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                const description = row.original.objectDescription
                const hoverText = [objectID, description].filter(Boolean).join(' — ')
                return (
                    <div className="flex flex-col min-w-0" title={hoverText || undefined}>
                        <span className="text-sm font-medium truncate">{objectID || '-'}</span>
                        {description && (
                            <span className="text-xs text-muted-foreground truncate"><TranslatableText text={description} /></span>
                        )}
                    </div>
                )
            },
        },
        {
            id: 'objectIcon',
            header: '',
            size: columnSizes.objectIcon,
            minSize: 40,
            maxSize: 40,
            cell: ({ row }) => {
                const iconFilename = row.original.iconFilename
                if (!iconFilename) return null
                return (
                    <div className="flex items-center justify-center">
                        <EntityIcon
                            src={buildIconPath(iconFilename)}
                            alt=""
                            className="h-8 w-8"
                        />
                    </div>
                )
            },
        },
        {
            id: 'qrCode',
            header: '',
            size: columnSizes.qrCode,
            minSize: 60,
            maxSize: 60,
            cell: ({ row }) => {
                const objectID = row.original.objectID
                if (!objectID) return <div>-</div>
                
                return (
                    <div className="flex items-center justify-center">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                                e.stopPropagation()
                                onShowQRCode(objectID)
                            }}
                            title={t('objects.showQRCode')}
                        >
                            <QrCode className="h-4 w-4" />
                        </Button>
                    </div>
                )
            },
        },
        // Metadata column only for Objects (services do not have metadata)
        ...(!isService ? [{
            id: 'metadata',
            header: '',
            size: columnSizes.metadata,
            minSize: 36,
            maxSize: 44,
            cell: ({ row }) => {
                const objectID = row.original.objectID
                if (!objectID || !onShowMetadata) return <div className="flex items-center justify-center p-0">-</div>
                return (
                    <div className="flex items-center justify-center p-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 min-w-0"
                            onClick={(e) => {
                                e.stopPropagation()
                                onShowMetadata(objectID)
                            }}
                            title={t('objects.viewMetadata', 'View metadata')}
                        >
                            <FileJson className="h-4 w-4" />
                        </Button>
                    </div>
                )
            },
        }] : []),
        ...(!isService ? [{
            id: 'recoveryChain',
            header: '',
            size: 40,
            minSize: 36,
            maxSize: 44,
            cell: ({ row }) => {
                const objectID = row.original.objectID
                if (!objectID || !onShowRecoveryChain) return <div className="flex items-center justify-center p-0">-</div>
                return (
                    <div className="flex items-center justify-center p-0">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 min-w-0"
                            onClick={(e) => {
                                e.stopPropagation()
                                onShowRecoveryChain(objectID)
                            }}
                            title={t('objects.viewRecoveryChain', 'View recovery chain')}
                        >
                            <FileStack className="h-4 w-4" />
                        </Button>
                    </div>
                )
            },
        }] : []),
        {
            accessorKey: 'datastore',
            header: '',
            size: 40,
            minSize: 40,
            maxSize: 40,
            enableHiding: false,
            cell: ({ row }) => {
                const datastore = row.getValue('datastore') as number | null
                // Check if datastore is 1 (Yes) - column is INT, 1 = Yes
                if (datastore === 1) {
                    return (
                        <div className="flex items-center justify-center">
                            <img 
                                src="/images/database.png" 
                                alt="Datastore" 
                                className="h-4 w-4"
                            />
                        </div>
                    )
                }
                return null
            },
        },
        // Two columns, never one blended status. SCAN is the object's own latest scan; HIERARCHY
        // is the roll-up — its own, made amber by a red dependency beneath — with the counts that
        // explain it. A green server over a red database is green here and amber there, and the
        // red "1" says why. Sortable separately, so "hierarchy red before a recovery" is one click.
        {
            accessorKey: 'objectAnalytics',
            header: () => <div className="text-center w-full" title={t('objects.statusPair.scanTitle', "This object's own latest scan")}>{t('objects.statusPair.scan', 'Scan')}</div>,
            size: columnSizes.status,
            minSize: 60,
            maxSize: 100,
            cell: ({ row }) => {
                const o = row.original
                if (o.objectAnalytics == null && o.objectStatus == null) return <div className="flex justify-center">-</div>
                return (
                    <div className="flex items-center justify-center">
                        <ObjectStatusPair variant="scan" size="h-4 w-4" scan={o.objectAnalytics} hierarchy={o.objectStatus != null ? Number(o.objectStatus) : null}
                            redHere={o.redHere} redBelow={o.redBelow} indicatorsBelow={o.indicatorsBelow} redBelowIds={o.redBelowIds} indicatorsBelowIds={o.indicatorsBelowIds} />
                    </div>
                )
            },
        },
        {
            accessorKey: 'objectStatus',
            header: () => <div className="text-center w-full" title={t('objects.statusPair.hierarchyTitle', 'Including everything it depends on. A red dependency makes this amber; amber dependencies do not roll up.')}>{t('objects.statusPair.hierarchy', 'Hierarchy')}</div>,
            size: columnSizes.status,
            minSize: 70,
            maxSize: 120,
            cell: ({ row }) => {
                const o = row.original
                const statusValue = o.objectStatus != null ? Number(o.objectStatus) : null
                if (statusValue === null) return <div className="flex justify-center">-</div>
                return (
                    <div className="flex items-center justify-center">
                        <ObjectStatusPair variant="hierarchy" size="h-4 w-4" scan={o.objectAnalytics} hierarchy={statusValue}
                            redHere={o.redHere} redBelow={o.redBelow} indicatorsBelow={o.indicatorsBelow} redBelowIds={o.redBelowIds} indicatorsBelowIds={o.indicatorsBelowIds} />
                    </div>
                )
            },
        },
        {
            accessorKey: 'locationName',
            header: t('objects.locationName'),
            size: columnSizes.location,
            minSize: 120,
            maxSize: 250,
            cell: ({ row }) => {
                const locationName = row.getValue('locationName') as string
                return (
                    <div className="text-sm text-muted-foreground">
                        {locationName ? <TranslatableText text={locationName} /> : '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'engineerRTO',
            header: () => <div className="text-right w-full">{t('objects.engineerRTO')}</div>,
            size: columnSizes.engineerRTO,
            minSize: 80,
            maxSize: 90,
            cell: ({ row }) => {
                const engineerRTO = row.getValue('engineerRTO') as number | null
                return (
                    <div className="text-right text-sm">
                        {engineerRTO !== null && engineerRTO !== undefined ? formatSecondsToDHM(engineerRTO) : '-'}
                    </div>
                )
            },
        },
        ...(!isService ? [{
            accessorKey: 'endToEndTime',
            header: () => <div className="text-right w-full">{t('objects.endToEndTime')}</div>,
            size: columnSizes.endToEndTime,
            minSize: 80,
            maxSize: 90,
            cell: ({ row }) => {
                const endToEndTime = row.getValue('endToEndTime') as number | null
                return (
                    <div className="text-right text-sm">
                        {endToEndTime !== null && endToEndTime !== undefined ? formatSecondsToDHM(endToEndTime) : '-'}
                    </div>
                )
            },
        }] : []),
        {
            accessorKey: 'totalCalculatedRTO',
            header: () => <div className="text-right w-full">{t('objects.rolledUpRTO')}</div>,
            size: columnSizes.totalRTO,
            minSize: 80,
            maxSize: 90,
            cell: ({ row }) => {
                const totalCalculatedRTO = row.getValue('totalCalculatedRTO') as number | null
                return (
                    <div className="text-right text-sm">
                        {totalCalculatedRTO !== null && totalCalculatedRTO !== undefined ? formatSecondsToDHM(totalCalculatedRTO) : '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'retention',
            header: () => <div className="text-right w-full">{t('objects.retention')}</div>,
            size: columnSizes.retention,
            minSize: 80,
            maxSize: 100,
            cell: ({ row }) => {
                const retention = row.getValue('retention') as number | null
                return (
                    <div className="text-right text-sm">
                        {retention !== null && retention !== undefined ? `${retention} days` : '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'imported',
            header: () => <div className="text-center w-full">{t('objects.imported', 'Imported')}</div>,
            size: columnSizes.imported,
            minSize: 80,
            maxSize: 90,
            cell: ({ row }) => {
                const imported = row.getValue('imported') as number | null
                if (imported !== 1) return <div className="flex justify-center">-</div>
                const hasRto = row.original.engineerRTO != null && row.original.engineerRTO > 0
                return (
                    <div className="flex items-center justify-center" title={!hasRto ? t('objects.importedIncomplete') : t('objects.importedComplete')}>
                        {/* amber = setup incomplete, blue = complete */}
                        <FileSpreadsheetIcon className={`h-4 w-4 ${!hasRto ? 'text-amber-500' : 'text-blue-500'}`} />
                    </div>
                )
            },
        },
        {
            id: 'actions',
            size: columnSizes.actions,
            minSize: 80,
            maxSize: 80,
            header: () => (
                <div className="text-center w-full">
                    {t('common.actions')}
                </div>
            ),
            cell: ({ row }) => {
                const object = row.original
                return (
                    <ObjectActions
                        object={object}
                        onDelete={onDelete}
                        onEdit={onEdit}
                        onView={onView}
                        onRetrieve={onRetrieve}
                    />
                )
            },
        },
    ]
}
